/**
 * Conteneur principal du wizard de création de garantie
 * Orchestre les steps, la navigation, et l'auto-save
 */

import { useCallback, useMemo, useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useWarrantyFormWizard } from '@/features/warranties/hooks/useWarrantyFormWizard';
import { useCreateWarranty } from '@/features/warranties/hooks/useWarrantyMutations';
import { warrantyApi } from '@/features/warranties/api/warrantyApi';
import { useNotifications } from '@/contexts/NotificationContext';
import { getWarrantyTypeWording } from '@/utils/vendorWording';
import { scrollToFirstError } from '@/utils/formUtils';
import { WARRANTY_TYPE_LABELS, VEHICLE_TYPE_LABELS } from '@/features/warranties/types/warranty.types';
import { WizardLayout } from '@/Components/shared/WizardLayout';
import { FormModeToggle } from '@/Components/shared/FormModeToggle';
import { useConfirm } from '@/hooks/useConfirm';
import { useWizardBrowserBack } from '@/hooks/useWizardBrowserBack';
import { Step1Informations } from '@/features/warranties/components/Step1Informations';
import { Step2Configuration } from '@/features/warranties/components/Step2Configuration';
import { Step3PartSelection } from '@/features/warranties/components/Step3PartSelection';
import { Step4Pricing } from '@/features/warranties/components/Step4Pricing';
import { SelectWarrantyPresetModal } from '@/features/warranties/components/SelectWarrantyPresetModal';
import { HelpCircle, Info, Settings, Package, CreditCard } from 'lucide-react';
import { useWarrantyTutorial } from '@/features/warranties/tutorials/hooks/useWarrantyTutorial';
import { TutorialSpotlight } from '@/features/warranties/tutorials/components/TutorialSpotlight';
import { TutorialIntro } from '@/features/warranties/tutorials/components/TutorialIntro';
import { getFilteredTutorialSteps } from '@/features/warranties/tutorials/constants/tutorialSteps';
import { useSocietyVendorType } from '@/features/societies/hooks/useSocietyVendorType';
import { useAuth } from '@/hooks/auth/useAuth';
import { useToggleTutorials } from '@/hooks/useToggleTutorials';
import { useSociety } from '@/features/societies/hooks/useSocieties';
import { useEffectiveConfiguration } from '@/features/societies/hooks/useSocietyConfiguration';
import type { WarrantyPreset } from '@/features/warranties/types/warranty-preset.types';

interface WizardContainerProps {
  formMode?: 'wizard' | 'preset';
  onModeToggle?: (mode: 'wizard' | 'preset') => void;
  onCreated?: (warrantyUuid: string) => void;
  societyId?: string;
  /** Mode embarqué dans un onglet (stepper/footer sticky au lieu de fixed) */
  embedded?: boolean;
}

export const WizardContainer = ({ formMode, onModeToggle, onCreated, societyId: societyIdProp, embedded }: WizardContainerProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showNotification } = useNotifications();
  const createWarrantyMutation = useCreateWarranty();

  // Récupérer le societyId depuis la prop ou l'URL, et returnTo depuis l'URL
  const [searchParams] = useSearchParams();
  const effectiveSocietyId = societyIdProp || searchParams.get('societyId') || undefined;
  const returnTo = searchParams.get('returnTo') || undefined;
  const isSocietyLocked = !!effectiveSocietyId;

  const {
    currentStep,
    formData,
    touchedSteps,
    canMensualize,
    updateFormData,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    toCreateDto,
    applyPreset,
    reset,
  } = useWarrantyFormWizard(effectiveSocietyId);

  // État de validation du step courant (remonté par chaque step)
  const [canProceed, setCanProceed] = useState(false);
  // Afficher les erreurs de validation (activé quand on clique "Suivant" sans validation)
  const [showErrors, setShowErrors] = useState(false);

  // Nettoyer le sessionStorage quand on quitte le formulaire (unmount)
  // Pattern StrictMode-safe : setTimeout annulé au remount, exécuté au vrai unmount
  const resetTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    clearTimeout(resetTimer.current);
    return () => {
      resetTimer.current = setTimeout(() => reset(), 0);
    };
  }, [reset]);

  // Protection bouton retour navigateur (active uniquement à partir du step 2) :
  // - back en step 2+ → retour au step précédent, données préservées
  // - step 1 → comportement navigateur natif (l'utilisateur peut quitter librement)
  // - beforeunload (refresh / fermeture tab) → alerte navigateur native (step 2+)
  useWizardBrowserBack({
    currentStep,
    goToPreviousStep,
    enabled: currentStep > 1,
    beforeUnloadEnabled: !createWarrantyMutation.isLoading,
  });

  // Détecter le type d'utilisateur
  const { user } = useAuth();
  const isExternalUser = useMemo(() => user?.type === 'EXTERNAL', [user?.type]);
  const isTutorialEnabled = isExternalUser && user?.showTutorials !== false;
  const { enableTutorials, disableTutorials } = useToggleTutorials();

  // État de la modale de sélection de preset (uniquement INTERNAL)
  const [showPresetModal, setShowPresetModal] = useState(user?.type === 'INTERNAL');

  // Ouvrir la modale quand on passe en mode preset
  useEffect(() => {
    if (formMode === 'preset') {
      setShowPresetModal(true);
    }
  }, [formMode]);

  // Vérifier si l'utilisateur peut choisir le type de garantie
  const { canChooseWarrantyType } = useSocietyVendorType(formData.societyId);

  // Récupérer les infos de la société pour le tutoriel
  const { data: society } = useSociety(formData.societyId);
  const { data: societyConfig } = useEffectiveConfiguration(formData.societyId);

  // Hook du tutoriel
  const {
    state: tutorialState,
    startTutorial,
    nextStep: nextTutorialStep,
    previousStep: previousTutorialStep,
    skipTutorial,
    completeTutorial,
    restartAtStep,
    showIntro,
  } = useWarrantyTutorial();

  // Obtenir les étapes du tutoriel filtrées
  const tutorialSteps = useMemo(
    () => getFilteredTutorialSteps(
      canMensualize,
      canChooseWarrantyType,
      isExternalUser,
      society ? {
        isTransferPrice: !!societyConfig?.isTransferPrice,
        defineProvision: society.defineProvision,
        commission: society.commission,
      } : undefined,
      formData.warrantyType,
    ),
    [canMensualize, canChooseWarrantyType, isExternalUser, societyConfig?.isTransferPrice, society?.defineProvision, society?.commission, formData.warrantyType]
  );

  // Reprendre le tutoriel au premier champ non rempli du wizard step actuel
  const findAndRestartAtLastIncomplete = useCallback(() => {
    // Trouver le premier step tutorial non valide sur le wizard step actuel
    const firstIncomplete = tutorialSteps.findIndex(
      (step) => step.wizardStep === currentStep && !step.validation(formData),
    );
    if (firstIncomplete !== -1) {
      restartAtStep(firstIncomplete);
      return;
    }
    // Tous valides : reprendre au dernier step du wizard step actuel
    let lastIndex = -1;
    tutorialSteps.forEach((step, i) => {
      if (step.wizardStep === currentStep) lastIndex = i;
    });
    restartAtStep(lastIndex !== -1 ? lastIndex : 0);
  }, [tutorialSteps, currentStep, formData, restartAtStep]);

  // Reset showErrors quand on change de step
  useEffect(() => {
    setShowErrors(false);
  }, [currentStep]);

  // Afficher l'intro du tutoriel si activé dans le profil et pas encore vu/skippé dans cette session
  useEffect(() => {
    if (isTutorialEnabled && !tutorialState.hasBeenCompleted && !tutorialState.hasBeenSkipped && !tutorialState.showIntro && !tutorialState.isActive) {
      // Délai pour laisser le DOM se charger
      const timer = setTimeout(() => {
        showIntro();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isTutorialEnabled, tutorialState.hasBeenCompleted, tutorialState.hasBeenSkipped, tutorialState.showIntro, tutorialState.isActive, showIntro]);

  // Étape actuelle du tutoriel
  const currentTutorialStep = useMemo(() => {
    if (!tutorialState.isActive) return null;
    return tutorialSteps[tutorialState.currentStepIndex];
  }, [tutorialState.isActive, tutorialState.currentStepIndex, tutorialSteps]);

  // Vérifier si le champ actuel du tutoriel est valide
  const isTutorialFieldValid = useMemo(() => {
    if (!currentTutorialStep) return false;
    return currentTutorialStep.validation(formData);
  }, [currentTutorialStep, formData]);

  // Gérer le passage à l'étape suivante du tutoriel
  const handleNextTutorialStep = useCallback(() => {
    if (tutorialState.currentStepIndex >= tutorialSteps.length - 1) {
      // Fin du tutoriel : aller à l'étape récapitulatif
      disableTutorials();
      completeTutorial();
      goToStep(6); // Step 6 = Récapitulatif
      const w = getWarrantyTypeWording(formData.warrantyType);
      showNotification('success', `Tutoriel terminé ! Vous pouvez maintenant créer votre ${w.singularLower}.`);
    } else {
      nextTutorialStep();

      // Changer de wizard step si nécessaire
      const nextStep = tutorialSteps[tutorialState.currentStepIndex + 1];
      if (nextStep && nextStep.wizardStep !== currentStep) {
        goToStep(nextStep.wizardStep);
      }
    }
  }, [
    tutorialState.currentStepIndex,
    tutorialSteps,
    disableTutorials,
    completeTutorial,
    nextTutorialStep,
    currentStep,
    goToStep,
    showNotification,
  ]);

  // Gérer le retour à l'étape précédente du tutoriel
  const handlePreviousTutorialStep = useCallback(() => {
    if (tutorialState.currentStepIndex > 0) {
      previousTutorialStep();

      // Changer de wizard step si nécessaire
      const prevStep = tutorialSteps[tutorialState.currentStepIndex - 1];
      if (prevStep && prevStep.wizardStep !== currentStep) {
        goToStep(prevStep.wizardStep);
      }
    }
  }, [
    tutorialState.currentStepIndex,
    tutorialSteps,
    previousTutorialStep,
    currentStep,
    goToStep,
  ]);

  // Définir les steps avec labels (4 steps fixes)
  const stepDefinitions = useMemo(() => {
    return [
      { number: 1, label: 'Informations', icon: <Info className="tw-w-6 tw-h-6" /> },
      { number: 2, label: 'Configuration', icon: <Settings className="tw-w-6 tw-h-6" /> },
      { number: 3, label: 'Pièces', icon: <Package className="tw-w-6 tw-h-6" /> },
      { number: 4, label: 'Tarification', icon: <CreditCard className="tw-w-6 tw-h-6" /> },
    ];
  }, []);

  // Logique du bouton suivant : afficher les erreurs si invalide
  const handleNext = useCallback(() => {
    if (!canProceed) {
      setShowErrors(true);
      scrollToFirstError();
      return;
    }
    setShowErrors(false);
    goToNextStep();
  }, [canProceed, goToNextStep]);

  /**
   * Gestion de la soumission finale
   */
  const handleSubmit = useCallback(async () => {
    try {
      const dto = toCreateDto();
      const result = await createWarrantyMutation.mutateAsync(dto);

      // Attendre que le cache soit mis à jour
      await queryClient.invalidateQueries({ queryKey: ['warranties'] });

      // Invalider le cache des garanties de la société si on vient d'une société
      if (effectiveSocietyId) {
        queryClient.invalidateQueries({
          queryKey: ['warranties-by-society', effectiveSocietyId],
        });
      }

      // Prefetch de la garantie complète avant navigation
      // Cela garantit que parts[], PDF et logs sont chargés
      await queryClient.prefetchQuery({
        queryKey: ['warranty', result.uuid],
        queryFn: () => warrantyApi.getWarranty(result.uuid)
      });

      // Nettoyer la session après succès
      reset();

      const wording = getWarrantyTypeWording(formData.warrantyType);
      showNotification('success', `${wording.singular} créée avec succès`);

      // Utiliser le callback si fourni, sinon navigation par défaut
      if (onCreated) {
        onCreated(result.uuid);
      } else if (returnTo) {
        navigate(returnTo);
      } else {
        navigate(`/warranties/${result.uuid}`);
      }
    } catch (error: any) {
      console.error('Erreur création garantie:', error);
      showNotification('error', error.response?.data?.message || error.message || 'Erreur lors de la création');
    }
  }, [toCreateDto, createWarrantyMutation, queryClient, showNotification, navigate, effectiveSocietyId, returnTo, onCreated, reset]);

  const { confirm, ConfirmDialogComponent } = useConfirm();
  const wording = useMemo(() => getWarrantyTypeWording(formData.warrantyType), [formData.warrantyType]);

  // Soumission avec confirmation (Step 4)
  const handleSubmitWithConfirm = useCallback(async () => {
    if (!canProceed) {
      setShowErrors(true);
      scrollToFirstError();
      return;
    }
    setShowErrors(false);

    const warrantyType = WARRANTY_TYPE_LABELS[formData.warrantyType || 0] || 'Inconnu';
    const vehicleType = formData.vehicleType !== undefined ? (VEHICLE_TYPE_LABELS[formData.vehicleType] || 'Inconnu') : undefined;
    const paymentMode = formData.isCashPayment && formData.isMensualisable
      ? 'Comptant ou mensuel'
      : formData.isCashPayment
      ? 'Comptant uniquement'
      : 'Mensuel uniquement';

    const SummaryRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
      <div className="tw-flex tw-justify-between tw-py-1.5">
        <span className="tw-text-gray-500">{label}</span>
        <span className="tw-font-medium tw-text-gray-900">{value}</span>
      </div>
    );

    const confirmationMessage = (
      <div className="tw-space-y-4">
        {/* Informations générales */}
        <div>
          <div className="tw-text-xs tw-font-semibold tw-text-gray-400 tw-uppercase tw-tracking-wider tw-mb-1">Informations</div>
          <div className="tw-divide-y tw-divide-gray-100">
            <SummaryRow label="Nom" value={formData.name} />
            <SummaryRow label="Type" value={warrantyType} />
            {vehicleType && <SummaryRow label="Véhicule" value={vehicleType} />}
          </div>
        </div>

        {/* Configuration */}
        <div>
          <div className="tw-text-xs tw-font-semibold tw-text-gray-400 tw-uppercase tw-tracking-wider tw-mb-1">Configuration</div>
          <div className="tw-divide-y tw-divide-gray-100">
            <SummaryRow label="Durée" value={`${formData.duration} mois`} />
            {formData.financialCap !== undefined && formData.financialCap > 0 && (
              <SummaryRow label="Plafond financier" value={`${formData.financialCap.toFixed(2)} €`} />
            )}
            {formData.kmLimit !== undefined && formData.kmLimit > 0 && (
              <SummaryRow label="Limite kilométrique" value={`${formData.kmLimit.toLocaleString('fr-FR')} km`} />
            )}
            {formData.provision !== undefined && formData.provision > 0 && (
              <SummaryRow label="Provision" value={`${formData.provision.toFixed(2)} €`} />
            )}
          </div>
        </div>

        {/* Pièces couvertes */}
        <div>
          <div className="tw-text-xs tw-font-semibold tw-text-gray-400 tw-uppercase tw-tracking-wider tw-mb-1">Pièces couvertes</div>
          <div className="tw-divide-y tw-divide-gray-100">
            <SummaryRow label="Pièces sélectionnées" value={formData.selectedPartIds.length} />
          </div>
        </div>

        {/* Tarification */}
        <div>
          <div className="tw-text-xs tw-font-semibold tw-text-gray-400 tw-uppercase tw-tracking-wider tw-mb-1">Tarification</div>
          <div className="tw-divide-y tw-divide-gray-100">
            <SummaryRow label="Prix de vente TTC" value={`${formData.sellingPrice?.toFixed(2)} €`} />
            <SummaryRow label="Mode de paiement" value={paymentMode} />
          </div>
        </div>
      </div>
    );

    const confirmed = await confirm({
      title: `Créer ${wording.withArticle} ?`,
      message: confirmationMessage,
      confirmLabel: 'Créer',
      cancelLabel: 'Annuler',
      variant: 'info',
    });

    if (confirmed) {
      await handleSubmit();
    }
  }, [canProceed, formData, confirm, wording, handleSubmit]);

  /**
   * Handlers pour la modale de sélection de preset
   */
  const handleSelectPreset = useCallback((preset: WarrantyPreset) => {
    applyPreset(preset);
    setShowPresetModal(false);
  }, [applyPreset]);

  const handleSkipPreset = useCallback(() => {
    setShowPresetModal(false);
    // Basculer automatiquement en mode wizard si on était en mode preset
    if (formMode === 'preset' && onModeToggle) {
      onModeToggle('wizard');
    }
  }, [formMode, onModeToggle]);

  /**
   * Rendu du step actuel
   */
  const renderCurrentStep = () => {
    const commonProps = {
      formData,
      updateFormData,
      onCanProceedChange: setCanProceed,
      showErrors,
    };

    // Mapping simple avec 4 steps
    switch (currentStep) {
      case 1:
        return (
          <Step1Informations
            {...commonProps}
            isSocietyLocked={isSocietyLocked}
          />
        );
      case 2:
        return <Step2Configuration {...commonProps} />;
      case 3:
        return <Step3PartSelection {...commonProps} />;
      case 4:
        return <Step4Pricing {...commonProps} />;
      default:
        return <div>Step non trouvé</div>;
    }
  };

  return (
    <WizardLayout
      steps={stepDefinitions}
      currentStep={currentStep}
      completedSteps={touchedSteps}
      onStepClick={goToStep}
      allowNavigation={true}
      embedded={embedded}
      onPrevious={currentStep > 1 ? goToPreviousStep : undefined}
      onNext={currentStep === 4 ? handleSubmitWithConfirm : handleNext}
      canProceed={canProceed}
      isSubmitting={createWarrantyMutation.isLoading}
      nextLabel={currentStep === 4 ? `Créer ${wording.withArticle}` : undefined}
      hideWhenTutorial={true}
      isTutorialActive={tutorialState.isActive}
      modeToggle={formMode && onModeToggle ? <FormModeToggle currentMode={formMode} onToggle={onModeToggle} /> : undefined}
      helpAction={!tutorialState.isActive && !tutorialState.showIntro && isExternalUser && currentStep < stepDefinitions.length ? (
        <button
          type="button"
          onClick={() => { enableTutorials(); findAndRestartAtLastIncomplete(); }}
          className="tw-flex tw-items-center tw-gap-2 tw-px-3 sm:tw-px-5 tw-py-3 tw-bg-white tw-text-affinicar-primary tw-font-medium tw-rounded-xl tw-border tw-border-affinicar-lighter hover:tw-bg-affinicar-lighter tw-transition-colors"
          title="Relancer le tutoriel"
        >
          <HelpCircle className="tw-w-4 tw-h-4" />
          <span className="tw-hidden sm:tw-inline">Aide</span>
        </button>
      ) : undefined}
    >
      {/* Zone de contenu */}
      {renderCurrentStep()}

      {ConfirmDialogComponent}

      {/* Introduction du tutoriel */}
      {tutorialState.showIntro && isTutorialEnabled && (
        <TutorialIntro
          onStart={() => startTutorial()}
          onSkip={() => { disableTutorials(); skipTutorial(); }}
        />
      )}

      {/* Modale de sélection de preset (uniquement INTERNAL au démarrage) */}
      {showPresetModal && (
        <SelectWarrantyPresetModal
          onSelectPreset={handleSelectPreset}
          onSkip={handleSkipPreset}
          societyId={formData.societyId}
        />
      )}

      {/* Tutoriel interactif */}
      {tutorialState.isActive && currentTutorialStep && isTutorialEnabled && (
        <TutorialSpotlight
          currentStep={currentTutorialStep}
          stepNumber={tutorialState.currentStepIndex + 1}
          totalSteps={tutorialSteps.length}
          isFieldValid={isTutorialFieldValid}
          onNext={handleNextTutorialStep}
          onPrevious={handlePreviousTutorialStep}
          onSkip={() => { disableTutorials(); skipTutorial(); }}
          placement={currentTutorialStep.placement}
        />
      )}
    </WizardLayout>
  );
};
